import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { scrollToError } from '@openx/components-core/utils/scrollToError';
import { noop } from '@openx/utils/noop';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealPromptPermission } from '../hooks/useDealPromptPermission';
import { getDefaultDealPromptAudienceDealFormValues } from '../utils/getDefaultDealPromptAudienceDealFormValues';
import { type DealPromptAudienceDealFormValues, EXPORT_TARGET_KEYS } from '../utils/types';

const getExportTargetLabels = (t: TFunction) =>
  ({
    cookie: t('Cookies'),
    device: t('MAIDs'),
    idl: t('RampID'),
    ctv: t('CTV'),
    ip: t('IP Address'),
    id5: t('ID5'),
  }) satisfies Record<keyof DealPromptAudienceDealFormValues['export_targets'], string>;

export type DealPromptAudienceCreateDealDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: DealPromptAudienceDealFormValues) => Promise<void>;
  dataPartnerAccountName?: string;
  isExplicitNonUsRegion?: boolean;
};

export const DealPromptAudienceCreateDealDialog = ({
  open,
  onClose,
  onSubmit,
  dataPartnerAccountName,
  isExplicitNonUsRegion = false,
}: DealPromptAudienceCreateDealDialogProps) => {
  const { t } = useTranslation();
  const targetIdLabels = useMemo(() => getExportTargetLabels(t), [t]);
  const { isInternalUser } = useDealPromptPermission();
  const formMethods = useForm<DealPromptAudienceDealFormValues>({
    defaultValues: getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion),
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    if (open) {
      reset(getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion));
    }
  }, [open, isExplicitNonUsRegion, reset]);

  const handleClose = useCallback(() => {
    reset(getDefaultDealPromptAudienceDealFormValues(isExplicitNonUsRegion));
    onClose();
  }, [isExplicitNonUsRegion, onClose, reset]);

  const submit = handleSubmit(async values => {
    try {
      const valuesToSubmit = isExplicitNonUsRegion
        ? {
            ...values,
            export_targets: getDefaultDealPromptAudienceDealFormValues(true).export_targets,
          }
        : values;

      await onSubmit(valuesToSubmit);
      handleClose();
    } catch {
      noop;
    }
  }, scrollToError);

  const showDataPartnerAlert = useMemo(() => {
    return dataPartnerAccountName && dataPartnerAccountName !== OPEN_AUDIENCE_ORG_NAME && isInternalUser;
  }, [dataPartnerAccountName, isInternalUser]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: 5,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {t('Create Deal from provided audience segments')}
          <IconButton onClick={handleClose} aria-label={t('Close')}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <FormProvider {...formMethods}>
        <DialogContent sx={{ pb: 1, px: 2 }}>
          {showDataPartnerAlert ? (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }} data-test="deal-prompt-audience-create-deal-partner-notice">
              {t('The audience and deal will be created under {partnerName}.', {
                partnerName: dataPartnerAccountName,
              })}
            </Alert>
          ) : null}

          <RHFTextField<DealPromptAudienceDealFormValues>
            name="name"
            label={t('Audience name')}
            fullWidth
            margin="normal"
            rules={{ required: t('Required') }}
            dataTest="deal-prompt-audience-create-deal-name"
          />

          {!isExplicitNonUsRegion ? (
            <>
              <Typography
                data-test="deal-prompt-audience-create-deal-target-id-title"
                variant="overline"
                sx={{
                  mt: 1,
                }}
              >
                {t('Target ID Types')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                {EXPORT_TARGET_KEYS.map(key => (
                  <RHFCheckboxField<DealPromptAudienceDealFormValues>
                    key={key}
                    name={`export_targets.${key}`}
                    label={
                      key === 'ip' ? (
                        <Box sx={{ width: 'max-content' }}>{targetIdLabels[key]}</Box>
                      ) : (
                        targetIdLabels[key]
                      )
                    }
                    margin="dense"
                    data-test={`deal-prompt-audience-create-deal-target-${key}`}
                  />
                ))}
              </Box>
            </>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button variant="text" color="secondary" onClick={handleClose} data-test="deal-prompt-audience-deal-cancel">
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={isSubmitting}
            data-test="deal-prompt-audience-deal-submit"
          >
            {t('Continue to deal')}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
